import React from 'react';
import { EntityChip } from '../components/redesign/EntityChip';
import { Button, Card } from '../components/redesign/ui';
import {
  Plus, ArrowsClockwise, PencilSimple, Trash, ShieldCheck,
  ToggleLeft, ToggleRight,
  Users as UsersIcon,
} from '@phosphor-icons/react';
import { ROLE_LABELS } from '../utils/constants';
import { COMPLAINT_GROUP_LABEL } from '../constants/ticketOptions';

// Neutral role pill; super_admin gets an accent icon.
function RolePill({ role }) {
  const label = ROLE_LABELS[role] || role;
  if (role === 'super_admin') {
    return (
      <span className="inline-flex items-center gap-1 rounded-chip border border-status-under-process/20 bg-status-under-process/8 px-2 py-0.5 text-[11px] font-medium text-status-under-process">
        <ShieldCheck size={11} weight="fill" />{label}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-chip bg-paper-muted px-2 py-0.5 text-[11px] font-medium text-ink-muted">
      {label}
    </span>
  );
}

// Offers capability cell — shows "Always" pill for implicit-capability roles
// (super_admin, entity_manager); an interactive toggle for other roles when
// the caller can edit them; a disabled read-only toggle otherwise.
function OffersCapabilityCell({ u, canEdit, onToggle }) {
  if (u.role === 'super_admin' || u.role === 'entity_manager') {
    return (
      <span className="inline-flex items-center gap-1 rounded-chip bg-paper-muted px-2 py-0.5 text-[11px] font-medium text-ink-muted">
        <ShieldCheck size={11} weight="fill" /> Always
      </span>
    );
  }
  const on = !!u.can_manage_offers;
  const Icon = on ? ToggleRight : ToggleLeft;
  const color = on ? 'text-status-under-process' : 'text-ink-faint';
  if (!canEdit) {
    return (
      <Icon size={22} weight="fill" className={`${color} opacity-60`}
        title={on ? 'Enabled (read-only)' : 'Disabled'} />
    );
  }
  return (
    <button onClick={() => onToggle(u)}
      title={on ? 'Disable Offers access' : 'Enable Offers access'}
      className={`inline-flex items-center transition hover:opacity-80 ${color}`}>
      <Icon size={22} weight="fill" />
    </button>
  );
}

// Active/Inactive dot+text — matches the handoff's status style.
function StatusIndicator({ active }) {
  const dot = active ? 'bg-status-resolved' : 'bg-status-closed';
  const text = active ? 'text-status-resolved' : 'text-ink-faint';
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs ${text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

// 2-char rounded-square avatar. Falls back to '?' when name is missing.
function Avatar({ name }) {
  const initials = (name || '?').trim().split(/\s+/).map((s) => s[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-paper-muted text-xs font-semibold text-ink">
      {initials}
    </div>
  );
}

// Per-user entity strip. See approved logic — see the props spec.
function EntityCell({ u, entities }) {
  // super_admin transcends entity assignment — always show "All entities".
  if (u.role === 'super_admin') return <AllEntitiesPill />;

  const allEntitySlugs = (entities || []).map((e) => e.slug).filter(Boolean);
  const uEntities = Array.isArray(u.entities) ? u.entities : null;

  if (uEntities && uEntities.length > 0) {
    if (allEntitySlugs.length > 0 && uEntities.length === allEntitySlugs.length) {
      return <AllEntitiesPill />;
    }
    return (
      <div className="flex flex-wrap gap-1">
        {uEntities.map((slug) => (
          <EntityChip key={slug} entitySlug={slug} className="!px-2 !py-0.5 !text-[10px]" />
        ))}
      </div>
    );
  }
  // Fallback for older rows without a proper entities array.
  const fallback = u.entity_display || u.entity_name || '—';
  return <span className="text-sm text-ink-muted">{fallback}</span>;
}

function AllEntitiesPill() {
  return (
    <span className="inline-flex rounded-chip bg-paper-muted px-2 py-0.5 text-[11px] font-semibold text-ink-muted">
      All entities
    </span>
  );
}

// Notified-groups cell — first 3 as chips + "+N" overflow, or "All (manager)"
// for entity_manager users with no explicit groups selected.
function NotifiedGroupsCell({ u }) {
  const groups = Array.isArray(u.complaint_groups) ? u.complaint_groups : [];
  if (groups.length === 0) {
    return (
      <span className="text-xs italic text-ink-faint">
        {u.role === 'entity_manager' ? 'All (manager)' : '—'}
      </span>
    );
  }
  return (
    <div className="flex flex-wrap gap-1">
      {groups.slice(0, 3).map((g) => (
        <span key={g} className="rounded-chip border border-status-under-process/20 bg-status-under-process/8 px-1.5 py-0.5 text-[11px] font-medium text-status-under-process">
          {COMPLAINT_GROUP_LABEL[g] || g}
        </span>
      ))}
      {groups.length > 3 && (
        <span className="rounded-chip bg-paper-muted px-1.5 py-0.5 text-[11px] font-medium text-ink-muted">
          +{groups.length - 3}
        </span>
      )}
    </div>
  );
}

function Th({ children, extra = '' }) {
  return (
    <th className={`px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-ink-faint first:px-5 ${extra}`}>{children}</th>
  );
}

function EmptyUsers() {
  return (
    <div className="flex flex-col items-center gap-2 py-12 text-center">
      <UsersIcon size={32} className="text-ink-faint" />
      <div className="text-sm font-medium text-ink-muted">No users yet.</div>
      <div className="text-xs text-ink-faint">Add your first team member to get started.</div>
    </div>
  );
}

export default function UsersView({
  users = [],
  entities = [],
  loading = false,
  currentUser,
  roleSummary = '',
  onRefresh,
  onAddUser,
  onEditUser,
  onDeactivate,
  onToggleOffers,
}) {
  const isSuperAdmin    = currentUser?.role === 'super_admin';
  const isEntityManager = currentUser?.role === 'entity_manager';
  const canAddUser      = isSuperAdmin || isEntityManager;
  // Row-level edit gate. super_admin: any user. entity_manager: same-entity
  // users, not super_admin, not self. Mirrors backend assertCanManageTarget.
  const canEditUser = (u) => {
    if (!u) return false;
    if (isSuperAdmin) return true;
    if (isEntityManager) {
      if (u.id === currentUser?.id) return false;
      if (u.role === 'super_admin') return false;
      if (u.entity_id !== currentUser?.entity_id) return false;
      return true;
    }
    return false;
  };
  const subhead = users.length === 0 ? 'No users yet' : roleSummary;

  return (
    <div className="p-1">
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-serif text-2xl text-ink">Users</h1>
            <p className="mt-0.5 text-[13px] text-ink-muted">{subhead}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onRefresh}
              title="Refresh"
              className="inline-flex h-9 w-9 items-center justify-center rounded-field border border-paper-border2 text-ink-muted hover:bg-paper-muted">
              <ArrowsClockwise size={16} className={loading ? 'animate-spin' : ''} />
            </button>
            {canAddUser && (
              <Button variant="primary" onClick={onAddUser}>
                <Plus size={14} weight="bold" />Add User
              </Button>
            )}
          </div>
        </div>

        {/* Users list card */}
        <Card className="overflow-hidden p-0">
          {loading ? (
            <div className="flex justify-center py-16">
              <ArrowsClockwise size={28} className="animate-spin text-status-under-process" />
            </div>
          ) : users.length === 0 ? (
            <EmptyUsers />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-paper-border bg-paper-muted/40">
                    <Th>User</Th>
                    <Th>Role</Th>
                    <Th>Department</Th>
                    <Th>Entities</Th>
                    <Th>Notified Groups</Th>
                    <Th>Status</Th>
                    <Th>Offers</Th>
                    <Th extra="text-right">Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-paper-border hover:bg-paper-muted/50">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar name={u.name} />
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-ink" dir="auto">{u.name}</div>
                            <div className="truncate text-xs text-ink-faint">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3"><RolePill role={u.role} /></td>
                      <td className="px-4 py-3">
                        {u.department
                          ? <span className="rounded-chip border border-paper-border2 bg-paper-muted px-2 py-0.5 text-xs text-ink-muted">{u.department}</span>
                          : <span className="text-xs text-ink-faint">—</span>}
                      </td>
                      <td className="px-4 py-3"><EntityCell u={u} entities={entities} /></td>
                      <td className="px-4 py-3"><NotifiedGroupsCell u={u} /></td>
                      <td className="px-4 py-3"><StatusIndicator active={!!u.is_active} /></td>
                      <td className="px-4 py-3">
                        <OffersCapabilityCell u={u} canEdit={canEditUser(u)} onToggle={onToggleOffers} />
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          {canEditUser(u) && (
                            <>
                              <button
                                onClick={() => onEditUser(u)}
                                title="Edit"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-field text-ink-faint transition hover:bg-paper-muted hover:text-status-under-process">
                                <PencilSimple size={14} />
                              </button>
                              {u.id !== currentUser?.id && u.is_active && (
                                <button
                                  onClick={() => onDeactivate(u.id)}
                                  title="Deactivate"
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-field text-ink-faint transition hover:bg-paper-muted hover:text-status-escalated">
                                  <Trash size={14} />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
